import { Badge, Banner, Button, Empty, Loader, Surface, Table, Text } from '@cloudflare/kumo'
import { ArrowsClockwise, Check, Flag } from '@phosphor-icons/react'
import { REPORT_REVIEW_HOURS, REPORT_TARGET_LABELS, type QueuedReport } from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { request } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken, type GetToken } from '../lib/auth'
import { fmtInt, fmtTime, toMessage } from '../lib/format'

const reportsApi = {
  queue: (getToken: GetToken) =>
    request<{ items: QueuedReport[]; total: number }>(getToken, '/admin/reports'),
  resolve: (getToken: GetToken, id: string) =>
    request<{ id: string; resolved: true; alreadyResolved: boolean }>(
      getToken,
      `/admin/reports/${encodeURIComponent(id)}/resolve`,
      { method: 'POST' },
    ),
}

/** 신고 대상을 처리하는 화면. 커뮤니티는 글·댓글 삭제와 작성자 제한, AI 결과는 원문·사진 확인 */
const targetPath = (r: QueuedReport) => (r.kind === 'ai' ? '/simulations' : '/community')

/** 신고 대기열 — 커뮤니티·AI 결과 신고 중 처리 표시가 없는 것. 오래된 것부터 */
export function ReportsPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [data, setData] = useState<{ items: QueuedReport[]; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<QueuedReport | null>(null)
  const [resolving, setResolving] = useState(false)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setData(await reportsApi.queue(getToken))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  const resolve = async () => {
    if (!pending) return
    setResolving(true)
    try {
      await reportsApi.resolve(getToken, pending.id)
      setPending(null)
      await reload()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setResolving(false)
    }
  }

  const hoursSince = (iso: string) =>
    Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 3_600_000))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="신고 대기열"
        description={
          data
            ? `처리하지 않은 신고 ${fmtInt(data.total)}건이에요. 오래된 것부터 보여요.`
            : '처리하지 않은 커뮤니티·AI 결과 신고예요.'
        }
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />
      <HelpPanel
        id="reports"
        steps={[
          <>신고는 24시간 안에 검토하기로 약속했어요. 24시간이 지난 신고는 빨간 배지가 붙어요.</>,
          <>
            "대상 열기" 로 [커뮤니티]·[시뮬레이션] 에서 내용을 확인하고, 필요하면 삭제·작성자 제한을
            해 주세요.
          </>,
          <>확인이 끝나면 "처리 표시" 를 눌러 대기열에서 빼요.</>,
        ]}
        notes={[
          '처리 표시는 되돌릴 수 없고 작업 기록에 남아요.',
          '24시간이 지나도 처리 표시가 없으면 운영 경보가 신고마다 한 번 가요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Surface className="overflow-x-auto rounded-lg p-0">
        {data === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : data.items.length === 0 ? (
          <Empty
            icon={<Flag size={40} className="text-kumo-inactive" />}
            title="처리할 신고가 없어요"
            description="새 신고가 들어오면 여기에 보여요."
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>신고 시각</Table.Head>
                <Table.Head>대상</Table.Head>
                <Table.Head>사유</Table.Head>
                <Table.Head>내용</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.items.map((r) => {
                const hours = hoursSince(r.createdAt)
                return (
                  <Table.Row key={r.id}>
                    <Table.Cell className="whitespace-nowrap text-xs">
                      <div>{fmtTime(r.createdAt)}</div>
                      <Badge variant={hours >= REPORT_REVIEW_HOURS ? 'error' : 'secondary'}>
                        {fmtInt(hours)}시간 전
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="max-w-48">
                      <div className="font-medium">
                        {REPORT_TARGET_LABELS[r.targetType] ?? r.targetType}
                        {r.blocked && ' · 작성자 차단'}
                      </div>
                      <div
                        className="text-kumo-subtle truncate font-mono text-xs"
                        title={r.targetId}
                      >
                        {r.targetId}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">{r.reasonLabel}</Table.Cell>
                    <Table.Cell className="max-w-xs">
                      <div
                        className="text-kumo-subtle truncate text-xs"
                        title={r.note ?? r.excerpt ?? ''}
                      >
                        {r.note ?? r.excerpt ?? '—'}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void navigate(targetPath(r))}
                        >
                          대상 열기
                        </Button>
                        <Button size="sm" icon={<Check />} onClick={() => setPending(r)}>
                          처리 표시
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <ConfirmDialog
        open={pending !== null}
        title="이 신고를 처리 표시할까요?"
        description="대기열에서 빠지고 24시간 경보도 가지 않아요. 되돌릴 수 없어요."
        confirmLabel="처리 표시"
        busy={resolving}
        onConfirm={() => void resolve()}
        onClose={() => setPending(null)}
      />
    </div>
  )
}
